import React, { useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CommentComposer,
  CommentIcon,
  CheckIcon,
  CommentList,
  DiscussIcon,
  StatusPill,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import useExecutionBlueprintReviewStore from "./_redux/ExecutionBlueprintReviewStore";
import { COMMENT_SCOPE, REVIEW_STATUS } from "../_redux/ExecutionBlueprintUrls";

// A single reviewable Execution Blueprint item - the EXW equivalent of
// Strategy Canvas's ReviewRow.js. Simpler than Canvas's version in one way
// (no growth_drivers-style structured JSON block - EXW items are plain
// label/value/rationale, per 08-fe-handoff.md §2) and richer in another
// (`rationale`, a field Canvas blocks don't have, shown as supporting
// context under the value).
//
// Comment UI reuses Canvas's CommentList/CommentComposer directly, with no
// fork - CommentList's edit button is now optional specifically so this
// could omit `onEdit` (see StrategyCanvasHelpers.js): EXW's comment
// endpoints have no PUT (COACH-API.md §4), so there's nothing for an edit
// button to call.
const ItemRow = ({
  uniqueId,
  item,
  expanded,
  onToggleExpand,
  readOnly,
  // True once the coach has attempted to submit with this row still unmarked.
  // Owned by the parent, not derived here from `review_status`, precisely so it
  // stays off until an attempt has been made — see the note on the root class.
  pending = false,
}) => {
  const [isCommenting, setIsCommenting] = useState(false);
  const [draft, setDraft] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const status = item.review_status;
  const comments = item.comments || [];

  const handleSetStatus = async (nextStatus) => {
    if (savingStatus || status === nextStatus) return;
    setSavingStatus(true);
    await useExecutionBlueprintReviewStore
      .getState()
      .setItemStatus(uniqueId, item.execution_blueprint_item_id, nextStatus);
    setSavingStatus(false);
  };

  // CommentComposer blocks the empty-draft case before this runs.
  const handleSaveComment = async () => {
    if (savingComment) return;
    setSavingComment(true);
    const saved = await useExecutionBlueprintReviewStore
      .getState()
      .addComment(uniqueId, {
        scopeType: COMMENT_SCOPE.ITEM,
        scopeKey: item.execution_blueprint_item_id,
        commentText: draft.trim(),
      });
    setSavingComment(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeletingId(commentId);
    await useExecutionBlueprintReviewStore
      .getState()
      .deleteComment(uniqueId, commentId);
    setDeletingId(null);
  };

  return (
    // `data-review-target` is the hook the Complete Review gate scrolls and
    // flashes by. Deliberately the same shape as the live review's
    // `data-agenda-target` (`item-<id>`), because it is the same job — send the
    // coach to one row among identical-looking siblings and make it obvious
    // which one they landed on.
    //
    // `rr-row--pending` is only ever set after the coach has TRIED to submit.
    // An unmarked row is the normal state of every row at the start of a
    // review, so reddening them on arrival would paint the whole screen as
    // errors before the coach has done anything wrong.
    <div
      className={[
        "rr-row",
        status ? `rr-${status}` : "",
        pending ? "rr-row--pending" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-review-target={`item-${item.execution_blueprint_item_id}`}
    >
      <button className="rr-row-header" onClick={onToggleExpand}>
        <span className="rr-row-left">
          <span className="rr-row-label">{item.label}</span>
        </span>
        <span className="rr-row-right">
          {status && (
            <StatusPill
              accepted={status === REVIEW_STATUS.ACCEPTED}
              className="rr-status-pill"
              acceptedClassName="rr-status-accepted"
              discussClassName="rr-status-to_discuss"
            />
          )}
          <span className="rr-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded && (
        <div className="rr-row-body">
          {item.value && <p className="rr-description">{item.value}</p>}
          {/* ⚠️ NO "Why:" LINE (2026-09-03, on instruction: removed "from every
                        block"). This rendered `item.rationale`.

                        `rationale` is still on the item, still round-trips through every
                        edit, and is still what the agent writes when it proposes a WIG —
                        nothing has been dropped from the data. It is simply no longer
                        printed on this surface. The CEO's own draft cards keep it, moved
                        behind a tooltip on the same pass.

                        The reasoning that applies everywhere it went: a rationale is
                        read once, while deciding whether to keep a WIG. Printed under
                        every item it doubled each row's height and pushed the values —
                        the things actually being compared — apart. */}

          {!readOnly && (
            <div className="rr-actions">
              <button
                className={`rr-action-btn rr-accept ${status === REVIEW_STATUS.ACCEPTED ? "active" : ""}`}
                onClick={() => handleSetStatus(REVIEW_STATUS.ACCEPTED)}
                disabled={savingStatus}
              >
                <CheckIcon size={12} />
                Accept
              </button>
              <button
                className={`rr-action-btn rr-discuss ${status === REVIEW_STATUS.TO_DISCUSS ? "active" : ""}`}
                onClick={() => handleSetStatus(REVIEW_STATUS.TO_DISCUSS)}
                disabled={savingStatus}
              >
                <DiscussIcon size={12} />
                To discuss with the CEO
              </button>
            </div>
          )}

          {!readOnly && comments.length === 0 && (
            <button
              className="rr-comment-link"
              onClick={() => setIsCommenting((prev) => !prev)}
            >
              <CommentIcon size={12} />
              Add a comment for the CEO
            </button>
          )}

          {comments.length > 0 && (
            <CommentList
              comments={comments}
              onDelete={handleDeleteComment}
              deletingId={deletingId}
              readOnly={readOnly}
            />
          )}
          {!readOnly && isCommenting && (
            <CommentComposer
              className="rr-comment-composer"
              value={draft}
              onChange={setDraft}
              onSave={handleSaveComment}
              saving={savingComment}
              placeholder="Add context, a question, or suggested revision"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ItemRow;
